import { useApp } from "../app-context";
import { listCompanies, recentCompanyChanges, saleName, type Company, type StageChange } from "../data";
import { CompanyLogo } from "../CompanyLogo";
import { VoiceBar } from "../voice/VoiceBar";
import { href } from "../router";
import { STAGES, stageLabel, totals, usd, type Stage } from "../stages";
import { ErrorNote, Loading, PageHeader, plural, useLoad } from "../ui";

/** The pipeline at a glance. Companies only: the pipeline is theirs, people are inside them. */
export function Dashboard() {
  const load = useLoad(async () => {
    const [companies, changes] = await Promise.all([listCompanies(), recentCompanyChanges(10)]);
    return { companies, changes };
  }, []);

  if (load.error) return <ErrorNote error={load.error} />;
  if (!load.data) return <Loading />;

  const t = totals(load.data.companies);
  const byId = new Map(load.data.companies.map((c) => [c.id, c]));

  return (
    <>
      <PageHeader title="Dashboard" sub={`${plural(load.data.companies.length, "company", "companies")} in the pipeline`} />
      <VoiceBar onApplied={() => void load.reload()} />

      <section className="money" aria-label="Totals">
        <div className="card stat hero">
          <span className="stat-label">Revenue to date</span>
          <strong className="stat-value" data-testid="revenue">{usd(t.revenue)}</strong>
          <span className="muted small">Payment Received</span>
        </div>
        <div className="card stat hero">
          <span className="stat-label">Pipeline value</span>
          <strong className="stat-value" data-testid="pipeline">{usd(t.pipeline)}</strong>
          <span className="muted small">Idea, In Talks and Complete</span>
        </div>
      </section>

      <section className="stage-cards" aria-label="Deals by stage">
        {STAGES.map((s, i) => (
          <div key={s.value} className="card stat" data-stage={s.value}>
            <span className="stat-label"><span className={`stage-dot s${i}`} aria-hidden="true" />{s.label}</span>
            <strong className="stat-value" data-testid={`count-${s.value}`}>{t.byStage[s.value].count}</strong>
            <span className="muted small">{usd(t.byStage[s.value].value)}</span>
          </div>
        ))}
      </section>

      <div className="dash-grid">
        <section className="card" aria-labelledby="chart-title">
          <h2 id="chart-title">Deals by stage</h2>
          <StageBars byStage={t.byStage} />
        </section>
        <section className="card" aria-labelledby="activity-title">
          <h2 id="activity-title">Recent activity</h2>
          <Activity changes={load.data.changes} byId={byId} />
        </section>
      </div>
    </>
  );
}

/**
 * Deal count per stage, one horizontal bar each. The stages are ordered, so
 * the bars use one ordinal ramp (light -> dark toward Payment Received),
 * validated with the dataviz skill's validate_palette.js --ordinal for both
 * themes. Single series: no legend; each bar is direct-labelled; the four
 * stage cards above are the table view.
 */
function StageBars({ byStage }: { byStage: Record<Stage, { count: number; value: number }> }) {
  const max = Math.max(1, ...STAGES.map((s) => byStage[s.value].count));
  return (
    <ul className="bars">
      {STAGES.map((s, i) => {
        const { count, value } = byStage[s.value];
        const tip = `${s.label}: ${plural(count, "deal")} · ${usd(value)}`;
        return (
          <li key={s.value} className="bar-row" tabIndex={0}>
            <span className="bar-label">{s.label}</span>
            <span className="bar-track" role="img" aria-label={tip}>
              {count > 0 ? <span className={`bar s${i}`} style={{ width: `${(count / max) * 100}%` }} /> : null}
            </span>
            <span className="bar-value">{count}</span>
            <span className="bar-tip" role="tooltip">{tip}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Activity({ changes, byId }: { changes: StageChange[]; byId: Map<number, Company> }) {
  const { salesById } = useApp();
  if (changes.length === 0) return <p className="muted empty">No stage changes yet.</p>;
  return (
    <ol className="activity">
      {changes.map((c) => {
        const company = byId.get(c.entity_id);
        const who = c.changed_by ? saleName(salesById.get(c.changed_by)) : null;
        return (
          <li key={c.id}>
            {company ? <CompanyLogo name={company.name} website={company.website} size="sm" /> : <span className="logo sm" aria-hidden="true" />}
            <span className="activity-text">
              <p>
                {company ? <a href={href({ page: "companies", id: company.id })}>{company.name}</a> : <span className="muted">A deleted company</span>}{" "}
                {c.from_stage
                  ? <>moved <strong>{stageLabel(c.from_stage)}</strong> → <strong>{stageLabel(c.to_stage)}</strong></>
                  : <>added at <strong>{stageLabel(c.to_stage)}</strong></>}
              </p>
              <p className="muted small">
                {who ? `${who} · ` : ""}<time dateTime={c.changed_at} title={new Date(c.changed_at).toLocaleString()}>{ago(c.changed_at)}</time>
              </p>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
export function ago(iso: string, now: number = Date.now()): string {
  const s = Math.round((new Date(iso).getTime() - now) / 1000);
  const abs = Math.abs(s);
  if (abs < 60) return "just now";
  if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(s / 3600), "hour");
  if (abs < 86400 * 30) return rtf.format(Math.round(s / 86400), "day");
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
